// App.js - Fixed version with proper conditional rendering
import React, { useState, useEffect } from 'react';
import { Settings, Search, Filter, Clock, TrendingUp, TrendingDown, CheckCircle, AlertTriangle, ChevronDown, MessageCircle, LogOut, User, UserCheck, LayoutGrid, Table } from 'lucide-react';

// Import components
import LoginForm from './components/auth/LoginForm';
import EmailVerificationPending from './components/auth/EmailVerificationPending';
import OnboardingFlow from './components/onboarding/OnboardingFlow';
import StatusColumn from './components/dashboard/StatusColumn';
import ComplaintModal from './components/modals/ComplaintModal';
import SettingsPage from './components/pages/SettingsPage';
import ProfileSettingsModal from './components/modals/ProfileSettingsModal';
import AnalyticsDashboard from './components/dashboard/AnalyticsDashboard';
import TrialBanner from './components/dashboard/TrialBanner';
import TrustpilotSettings from './components/settings/TrustpilotSettings';
// Import workspace components
import WorkspaceSwitcher from './components/workspace/WorkspaceSwitcher';
import CreateWorkspaceModal from './components/modals/CreateWorkspaceModal';
import WorkspaceRenameModal from './components/modals/WorkspaceRenameModal';
import ReviewsDashboard from './components/reviews/ReviewsDashboard';
import UsageBanner from './components/dashboard/UsageBanner';
import UsageAnalyticsWidget from './components/dashboard/UsageAnalyticsWidget';
import { STATUS_COLUMNS, PRIORITY_COLORS } from './utils/constants';
// Import API service
import api from './services/api';
// Import analytics
import { trackUpgradeConversion } from './utils/analytics';



// Loading Screen Component
const DashboardLoadingScreen = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="mb-8 relative">
          <div className="w-20 h-20 bg-purple-600 rounded-xl flex items-center justify-center mx-auto animate-pulse shadow-lg">
            <MessageCircle className="w-12 h-12 text-white fill-white" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
          </div>
        </div>
        
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Loading TicketDesk</h2>
        <p className="text-gray-600 mb-4">Fetching your tickets...</p>
        
        <div className="w-64 mx-auto bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="bg-purple-600 h-2 rounded-full" style={{
            animation: 'loading-bar 2s ease-in-out infinite'
          }}></div>
        </div>
        
        <div className="mt-6 flex justify-center space-x-2">
          <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
      
      <style>{`
        @keyframes loading-bar {
          0% {
            width: 0%;
            margin-left: 0%;
          }
          50% {
            width: 60%;
            margin-left: 20%;
          }
          100% {
            width: 100%;
            margin-left: 100%;
          }
        }
      `}</style>
    </div>
  );
};

// Table View Component
const TicketsTable = ({ 
  complaints, 
  onComplaintClick, 
  onStatusUpdate,
  getCustomerName,
  getCustomerEmail 
}) => {
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedComplaints = [...complaints].sort((a, b) => {
    let aVal, bVal;

    switch (sortField) {
      case 'subject':
        aVal = a.subject || '';
        bVal = b.subject || '';
        break;
      case 'customer':
        aVal = getCustomerName(a) || '';
        bVal = getCustomerName(b) || '';
        break;
      case 'status':
        aVal = a.status || '';
        bVal = b.status || '';
        break;
      case 'priority':
        const priorityOrder = { 'URGENT': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        aVal = priorityOrder[a.priority] || 0;
        bVal = priorityOrder[b.priority] || 0;
        break;
      case 'createdAt':
        aVal = new Date(a.createdAt || a.created_at);
        bVal = new Date(b.createdAt || b.created_at);
        break;
      default:
        return 0;
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      'NEW': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'New' },
      'IN_PROGRESS': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'In Progress' },
      'RESOLVED': { bg: 'bg-green-100', text: 'text-green-800', label: 'Resolved' },
      'CLOSED': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' }
    };
    const config = statusConfig[status] || statusConfig['NEW'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    if (!priority) return null;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        PRIORITY_COLORS[priority?.toUpperCase()] || 'bg-gray-100 text-gray-800'
      }`}>
        {priority}
      </span>
    );
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusChange = (complaintId, newStatus) => {
    onStatusUpdate(complaintId, newStatus);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                onClick={() => handleSort('subject')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Ticket <SortIcon field="subject" />
              </th>
              <th 
                onClick={() => handleSort('customer')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Customer <SortIcon field="customer" />
              </th>
              <th 
                onClick={() => handleSort('status')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Status <SortIcon field="status" />
              </th>
              <th 
                onClick={() => handleSort('priority')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Priority <SortIcon field="priority" />
              </th>
              <th 
                onClick={() => handleSort('createdAt')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Created <SortIcon field="createdAt" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedComplaints.map((complaint) => (
              <tr 
                key={complaint.id} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onComplaintClick(complaint)}
              >
                <td className="px-6 py-4">
                  <div className="max-w-xs">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {complaint.subject || 'No Subject'}
                    </div>
                    <div className="text-sm text-gray-500 truncate">
                      {complaint.description || complaint.content || 'No description'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">
                      {getCustomerName(complaint) || 'Unknown'}
                    </div>
                    <div className="text-gray-500">
                      {getCustomerEmail(complaint)}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <select
                    value={complaint.status}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleStatusChange(complaint.id, e.target.value);
                    }}
                    className="text-sm border-0 bg-transparent focus:ring-0 focus:outline-none cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="NEW">New</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CLOSED">Closed</option>
                  </select>
                </td>
                <td className="px-6 py-4">
                  {getPriorityBadge(complaint.priority)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {formatDate(complaint.createdAt || complaint.created_at)}
                </td>
                <td className="px-6 py-4 text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onComplaintClick(complaint);
                    }}
                    className="text-purple-600 hover:text-purple-900"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {complaints.length === 0 && (
        <div className="text-center py-12">
          <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your search or filters to find tickets.
          </p>
        </div>
      )}
    </div>
  );
};

const RealtimeNotification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const getNotificationStyles = () => {
    switch (type) {
      case 'new_complaint':
        return 'bg-purple-600 text-white';
      case 'status_update':
        return 'bg-blue-600 text-white';
      case 'import_complete':
        return 'bg-green-600 text-white';
      case 'workspace_switch':
        return 'bg-blue-600 text-white';
      case 'success':
        return 'bg-green-600 text-white';
      case 'error':
        return 'bg-red-600 text-white';
      case 'upgrade_required':
        return 'bg-amber-600 text-white';
      case 'permission_error':
        return 'bg-orange-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  return (
    <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${getNotificationStyles()} animate-slide-in`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <MessageCircle className="w-5 h-5 mr-2" />
          <span className="text-sm font-medium">{message}</span>
        </div>
        <button 
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200"
        >
          ×
        </button>
      </div>
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// Main App Component
function App() {
  const [showTrustpilotSettings, setShowTrustpilotSettings] = useState(false);
  const [user, setUser] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [activeTab, setActiveTab] = useState('complaints');
  const [viewMode, setViewMode] = useState('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showMyCases, setShowMyCases] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  
  const [needsVerification, setNeedsVerification] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  
  const [showRenameWorkspace, setShowRenameWorkspace] = useState(false);
  const [workspaceToRename, setWorkspaceToRename] = useState(null);
  
  const [userSubscription, setUserSubscription] = useState(null);
  const [notification, setNotification] = useState(null);
  
  const [filters, setFilters] = useState({
    priority: 'all',
    assignedTo: 'all'
  });

  const [closedTimeRange, setClosedTimeRange] = useState('24h'); 

  const [stats, setStats] = useState({
    new: 0,
    newChange: 0,
    inProgress: 0,
    inProgressChange: 0,
    resolvedToday: 0,
    resolvedChange: 0,
    avgResponseTime: 0,
    responseTimeChange: 0
  });

  const handleNeedsVerification = (email) => {
    setPendingVerificationEmail(email);
    setNeedsVerification(true);
  };

  const handleWorkspaceError = (error, operation = 'workspace operation') => {
    console.error(`Failed ${operation}:`, error);
    
    let message = `Failed to ${operation}`;
    let type = 'error';
    
    if (error.message) {
      if (error.message.includes('Scale or Enterprise') || 
          error.message.includes('requires Scale') ||
          error.message.includes('Multiple workspaces require')) {
        message = 'Multiple workspaces require Scale or Enterprise plan';
        type = 'upgrade_required';
      } else if (error.message.includes('workspace limit') ||
                 error.message.includes('limit') && error.message.includes('reached')) {
        message = `Workspace limit reached for your current plan`;
        type = 'upgrade_required';
      } else if (error.message.includes('permission') ||
                 error.message.includes('access denied')) {
        message = 'You don\'t have permission for this operation';
        type = 'permission_error';
      } else if (error.message.includes('Creating workspaces requires')) {
        message = 'Creating workspaces requires Scale or Enterprise plan';
        type = 'upgrade_required';
      }
    }
    
    setNotification({ message, type });
  };

  const hasWorkspaceAccess = () => {
    return userSubscription && ['SCALE', 'ENTERPRISE'].includes(userSubscription.plan);
  };

  const canCreateWorkspace = () => {
    if (!hasWorkspaceAccess()) return false;
    
    const limit = getWorkspaceLimit();
    return workspaces.length < limit;
  };

  const getWorkspaceLimit = () => {
    if (!userSubscription) return 1;
    
    switch (userSubscription.plan) {
      case 'SCALE': return 5;
      case 'ENTERPRISE': return 20;
      default: return 1;
    }
  };

  const showUpgradeNotification = (context = 'workspace') => {
    let message = 'Upgrade to Scale or Enterprise to access this feature';
    
    switch (context) {
      case 'multiple_workspaces':
        message = 'Multiple workspaces available on Scale and Enterprise plans';
        break;
      case 'create_workspace':
        message = 'Creating workspaces requires Scale or Enterprise plan';
        break;
      case 'workspace_limit':
        message = `Workspace limit (${getWorkspaceLimit()}) reached for your plan`;
        break;
    }
    
    setNotification({ message, type: 'upgrade_required' });
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user && teamMembers.length === 0) {
      loadTeamMembers();
    }
  }, [user]);

  useEffect(() => {
    if (complaints.length > 0) {
      calculateStats();
    }
  }, [complaints]);

  useEffect(() => {
    if (user) {
      setupRealTimeUpdates();
    }
  }, [user]);

  useEffect(() => {
    if (user && !showOnboarding) {
      loadWorkspaces();
    }
  }, [user, showOnboarding]);

  useEffect(() => {
    if (currentWorkspace && user && !showOnboarding) {
      loadComplaints();
    }
  }, [currentWorkspace]);

  // Handle upgrade success/failure
useEffect(() => {
  const handleUpgrade = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const upgradeStatus = urlParams.get('upgrade');

    if (upgradeStatus === 'success') {
      // Track the upgrade conversion
      // Get previous plan before loadWorkspaces updates it
      const previousPlan = userSubscription?.plan || api.getUserSubscription()?.plan || 'FREE';

      setNotification({
        message: 'Payment successful! Your subscription is now active.',
        type: 'success'
      });
      window.history.replaceState({}, '', '/app');

      // Load updated subscription data
      await loadWorkspaces();

      // Get new plan after workspace load
      const newPlan = api.getUserSubscription()?.plan;

      // Track upgrade conversion (only if FREE to paid)
      if (newPlan && previousPlan) {
        trackUpgradeConversion({
          plan: newPlan,
          previousPlan: previousPlan,
          value: 0, // You can add pricing logic here if needed
          userId: user?.id
        });
      }
    } else if (upgradeStatus === 'cancelled') {
      setNotification({
        message: 'Payment cancelled. You can upgrade anytime from Settings.',
        type: 'error'
      });
      window.history.replaceState({}, '', '/app');
    }
  };

  handleUpgrade();
}, []);


  const calculateStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const newComplaints = complaints.filter(c => c.status === 'NEW').length;
    const inProgressComplaints = complaints.filter(c => c.status === 'IN_PROGRESS').length;
    const resolvedToday = complaints.filter(c => {
      if (c.status === 'RESOLVED' && c.resolvedAt) {
        const resolvedDate = new Date(c.resolvedAt);
        resolvedDate.setHours(0, 0, 0, 0);
        return resolvedDate.getTime() === today.getTime();
      }
      return false;
    }).length;

    const resolvedComplaints = complaints.filter(c => c.status === 'RESOLVED' && c.resolvedAt);
    let avgTime = 0;
    if (resolvedComplaints.length > 0) {
      const totalTime = resolvedComplaints.reduce((acc, c) => {
        const created = new Date(c.createdAt);
        const resolved = new Date(c.resolvedAt);
        return acc + (resolved - created) / (1000 * 60 * 60);
      }, 0);
      avgTime = totalTime / resolvedComplaints.length;
    }

    setStats({
      new: newComplaints,
      newChange: 12,
      inProgress: inProgressComplaints,
      inProgressChange: -8,
      resolvedToday: resolvedToday,
      resolvedChange: 24,
      avgResponseTime: avgTime.toFixed(1),
      responseTimeChange: -15
    });
  };

  const loadWorkspaces = async () => {
    try {
      const response = await api.getWorkspacesList();
      if (response.success && response.workspaces) {
        setWorkspaces(response.workspaces);
        api.setWorkspaces(response.workspaces);

        if (response.subscription) {
          setUserSubscription(response.subscription);
          api.setUserSubscription(response.subscription);
        }

        let targetWorkspace;
        
        if (!hasWorkspaceAccess()) {
          targetWorkspace = response.workspaces.find(w => w.isDefault);
        } else {
          targetWorkspace = response.workspaces.find(w => w.isCurrent) 
                         || response.workspaces.find(w => w.isDefault) 
                         || response.workspaces[0];
        }

        if (targetWorkspace) {
          setCurrentWorkspace(targetWorkspace);
          api.setCurrentWorkspace(targetWorkspace);
           await loadComplaints();
        }
      }
    } catch (error) {
      console.error('Failed to load workspaces:', error);
      handleWorkspaceError(error, 'load workspaces');
    }
  };

  const handleWorkspaceSwitch = async (workspace) => {
    if (workspace.id === currentWorkspace?.id) {
      return;
    }

    if (!hasWorkspaceAccess() && workspaces.length > 1) {
      showUpgradeNotification('multiple_workspaces');
      return;
    }

    setWorkspaceLoading(true);
    try {
      const response = await api.switchWorkspace(workspace.id);
      if (response.success) {
        setCurrentWorkspace(response.workspace);
        api.setCurrentWorkspace(response.workspace);
        
        setComplaints([]);
        setSelectedComplaint(null);
        
        setNotification({
          message: `Switched to ${response.workspace.name}`,
          type: 'workspace_switch'
        });
      }
    } catch (error) {
      handleWorkspaceError(error, 'switch workspace');
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const handleCreateWorkspace = async (workspaceData) => {
    if (!hasWorkspaceAccess()) {
      showUpgradeNotification('create_workspace');
      throw new Error('Creating workspaces requires Scale or Enterprise plan');
    }

    if (!canCreateWorkspace()) {
      showUpgradeNotification('workspace_limit');
      throw new Error(`Workspace limit (${getWorkspaceLimit()}) reached for your plan`);
    }

    try {
      const response = await api.createWorkspace(
        workspaceData.name,
        workspaceData.description,
        workspaceData.slug
      );
      
      if (response.success) {
        await loadWorkspaces();
        setShowCreateWorkspace(false);
        
        setNotification({
          message: `Created workspace "${response.workspace.name}"`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Failed to create workspace:', error);
      handleWorkspaceError(error, 'create workspace');
      throw error;
    }
  };

  const handleRenameWorkspace = (workspace) => {
    setWorkspaceToRename(workspace);
    setShowRenameWorkspace(true);
  };

  const handleWorkspaceRenamed = async (updatedWorkspace) => {
    try {
      if (currentWorkspace?.id === updatedWorkspace.id) {
        setCurrentWorkspace(updatedWorkspace);
        api.setCurrentWorkspace(updatedWorkspace);
      }
      
      const updatedWorkspaces = workspaces.map(w => 
        w.id === updatedWorkspace.id ? { ...w, ...updatedWorkspace } : w
      );
      setWorkspaces(updatedWorkspaces);
      api.setWorkspaces(updatedWorkspaces);
      
      setNotification({
        message: `Workspace renamed to "${updatedWorkspace.name}"`,
        type: 'success'
      });
      
    } catch (error) {
      console.error('Failed to update workspace after rename:', error);
      loadWorkspaces();
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await api.getTeamMembers();
      if (response && response.members) {
        setTeamMembers(response.members);
      }
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  };

  const setupRealTimeUpdates = () => {
    const handleUpdate = (update) => {
      const { type, complaint } = update;
      
      if (type === 'new') {
        setComplaints(prevComplaints => {
          const exists = prevComplaints.some(c => c.id === complaint.id);
          if (exists) return prevComplaints;
          
          return [complaint, ...prevComplaints];
        });
        
        setNotification({
          message: `New Ticket: ${complaint.subject || 'Untitled'}`,
          type: 'new_complaint'
        });
        
      } else if (type === 'message_added') {
        setNotification({
          message: `New message in conversation`,
          type: 'new_complaint'
        });
        
        if (selectedComplaint && selectedComplaint.id === update.complaint?.complaintId) {
          loadComplaints(true);
        }
        
      } else if (type === 'import_complete') {
        loadComplaints(true);
        setNotification({
          message: `Gmail import completed - ${update.complaint?.totalImported || 0} new tickets added`,
          type: 'import_complete'
        });
      }
    };

    api.onComplaintUpdate(handleUpdate);
    
    return () => {
      api.offComplaintUpdate(handleUpdate);
    };
  };

const checkAuth = async () => {
  const token = localStorage.getItem('auth_token');
  
  if (!token) {
    setLoading(false);
    return;
  }
  
  try {
    // Get user data
    const userData = await api.getCurrentUser();
    setUser(userData);
    
    const storedSubscription = api.getUserSubscription();
    if (storedSubscription) {
      setUserSubscription(storedSubscription);
    }
    
    // Check for any connected integrations
    const [gmailStatus, fbStatus, trustpilotStatus] = await Promise.all([
      api.checkGmailStatus().catch(() => ({ connected: false })),
      api.getFacebookStatus().catch(() => ({ connected: false })),
      api.getTrustpilotStatus().catch(() => ({ connected: false }))
    ]);
    
    const hasAnyIntegration = gmailStatus?.connected || 
                             (fbStatus?.connected && fbStatus?.pages?.length > 0) ||
                             trustpilotStatus?.connected;
    
    if (hasAnyIntegration) {
      // Has integrations = skip onboarding, go to dashboard
      setShowOnboarding(false);
      await loadWorkspaces();
    } else {
      // No integrations = show onboarding
      setShowOnboarding(true);
    }
    
  } catch (error) {
    console.error('Auth check failed:', error);
    localStorage.removeItem('auth_token');
    api.clearToken();
  }
  
  setLoading(false);
};

const loadComplaints = async (silent = false) => {
  if (!currentWorkspace) {
    return;
  }
  
  if (!silent) {
    setComplaintsLoading(true);
  }
  
  try {
    const minLoadTime = silent ? Promise.resolve() : new Promise(resolve => setTimeout(resolve, 800));
    const complaintsPromise = api.getComplaints();
    
    const [data] = await Promise.all([complaintsPromise, minLoadTime]);
    
    if (data && data.success && data.complaints) {
      setComplaints(data.complaints);
    } else if (Array.isArray(data)) {
      setComplaints(data);
    } else {
      console.error('Unexpected complaint data format:', data);
      setComplaints([]);
    }
    
    // Mark that we've loaded initial data
    if (!hasLoadedInitialData) {
      setHasLoadedInitialData(true);
    }
  } catch (error) {
    console.error('Failed to load complaints:', error);
    setComplaints([]);
    
    // Still mark as loaded even on error
    if (!hasLoadedInitialData) {
      setHasLoadedInitialData(true);
    }
  } finally {
    if (!silent) {
      setComplaintsLoading(false);
    }
  }
};

  const handleComplaintUpdate = () => {
    loadComplaints(true);
  };

const handleStatusUpdate = async (complaintId, newStatus) => {
  // Find the complaint and store previous state for rollback
  const previousComplaint = complaints.find(c => c.id === complaintId);
  if (!previousComplaint) return;

  const previousStatus = previousComplaint.status;

  // Optimistic update - immediately update UI
  setComplaints(prevComplaints =>
    prevComplaints.map(complaint =>
      complaint.id === complaintId
        ? {
            ...complaint,
            status: newStatus,
            resolvedAt: newStatus === 'RESOLVED' ? new Date() : complaint.resolvedAt
          }
        : complaint
    )
  );

  // Make API call in background
  try {
    await api.updateComplaintStatus(complaintId, newStatus);
  } catch (error) {
    console.error('Failed to update status:', error);

    // Rollback on error
    setComplaints(prevComplaints =>
      prevComplaints.map(complaint =>
        complaint.id === complaintId
          ? {
              ...complaint,
              status: previousStatus,
              resolvedAt: previousComplaint.resolvedAt
            }
          : complaint
      )
    );

    // Show error notification
    setNotification({
      message: 'Failed to update ticket status. Please try again.',
      type: 'error'
    });
  }
};

const getComplaintsByStatus = (status) => {
  let filtered = complaints.filter(c => {
    if (c.status !== status) return false;
    
    // Filter CLOSED tickets by time range
    if (status === 'CLOSED' && closedTimeRange !== 'all') {
      const updatedAt = new Date(c.updatedAt || c.resolvedAt || c.createdAt);
      const now = Date.now();
      const hoursDiff = (now - updatedAt) / (1000 * 60 * 60);
      
      switch (closedTimeRange) {
        case '24h':
          if (hoursDiff > 24) return false;
          break;
        case '7d':
          if (hoursDiff > 168) return false; // 7 * 24
          break;
        case '30d':
          if (hoursDiff > 720) return false; // 30 * 24
          break;
      }
    }
    
    return true;
  });
  
  if (showMyCases && user) {
    filtered = filtered.filter(complaint => {
      const assignedTo = complaint.assignedTo || complaint.assignedAgent?.id;
      return assignedTo === user.id || assignedTo === user.email || assignedTo === user.name;
    });
  }
  
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(complaint => {
      const subject = (complaint.subject || '').toLowerCase();
      const content = (complaint.content || complaint.description || '').toLowerCase();
      const customerName = getCustomerName(complaint).toLowerCase();
      const customerEmail = getCustomerEmail(complaint).toLowerCase();
      const ticketId = (complaint.ticketId || '').toLowerCase(); // ✅ ADD TICKET ID
      
      return subject.includes(query) || 
             content.includes(query) || 
             customerName.includes(query) ||
             customerEmail.includes(query) ||
             ticketId.includes(query); // ✅ SEARCH BY TICKET ID
    });
  }
  
  if (filters.priority !== 'all') {
    filtered = filtered.filter(c => 
      c.priority?.toUpperCase() === filters.priority
    );
  }
  
  if (filters.assignedTo !== 'all') {
    if (filters.assignedTo === 'unassigned') {
      filtered = filtered.filter(c => !c.assignedTo && !c.assignedAgent);
    } else {
      filtered = filtered.filter(c => {
        const assignedId = c.assignedTo || c.assignedAgent?.id;
        return assignedId === filters.assignedTo;
      });
    }
  }
  
  return filtered;
};
  
  const getCustomerName = (complaint) => {
    if (typeof complaint.customer === 'object' && complaint.customer?.name) {
      return complaint.customer.name;
    }
    return complaint.customerName || complaint.customer_name || '';
  };
  
  const getCustomerEmail = (complaint) => {
    if (typeof complaint.customer === 'object' && complaint.customer?.email) {
      return complaint.customer.email;
    }
    return complaint.customerEmail || complaint.customer_email || '';
  };

  const handleLogout = () => {
    api.clearToken();
    setUser(null);
    setCurrentWorkspace(null);
    setWorkspaces([]);
    setUserSubscription(null);
    setUserMenuOpen(false);
    setNeedsVerification(false);
    setPendingVerificationEmail('');
    window.location.reload();
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setNeedsVerification(false);
    setPendingVerificationEmail('');
  };

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
      
      if (!userData.onboardingCompleted) {
        setUser({ ...userData, onboardingCompleted: true });
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(prev => ({ ...prev, onboardingCompleted: true }));
    }
  };

  const getUniqueAssignees = () => {
    const assignees = new Map();
    
    assignees.set('unassigned', { id: 'unassigned', name: 'Unassigned' });
    
    if (user) {
      const userHasAssignments = complaints.some(c => {
        const assignedTo = c.assignedTo || c.assignedAgent?.id;
        return assignedTo === user.id || assignedTo === user.email || assignedTo === user.name;
      });
      
      if (userHasAssignments) {
        assignees.set(user.id, { 
          id: user.id, 
          name: user.name || user.email,
          isCurrentUser: true
        });
      }
    }
    
    teamMembers.forEach(member => {
      if (!assignees.has(member.id)) {
        assignees.set(member.id, { 
          id: member.id, 
          name: member.name || member.email 
        });
      }
    });
    
    complaints.forEach(complaint => {
      if (complaint.assignedTo && !assignees.has(complaint.assignedTo)) {
        assignees.set(complaint.assignedTo, { 
          id: complaint.assignedTo, 
          name: complaint.assignedTo 
        });
      }
      if (complaint.assignedAgent && !assignees.has(complaint.assignedAgent.id)) {
        assignees.set(complaint.assignedAgent.id, {
          id: complaint.assignedAgent.id,
          name: complaint.assignedAgent.name || complaint.assignedAgent.email
        });
      }
    });
    
    return Array.from(assignees.values());
  };

const isInitialLoading = loading || 
                        (user && !showOnboarding && !hasLoadedInitialData);

if (isInitialLoading) {
  return <DashboardLoadingScreen />;
}

  if (!user) {
    if (needsVerification) {
      return (
        <EmailVerificationPending 
          email={pendingVerificationEmail}
          api={api}
          onResendEmail={() => {
            console.log('Verification email resent to:', pendingVerificationEmail);
          }}
        />
      );
    }
    
    return (
      <LoginForm 
        onLoginSuccess={handleLoginSuccess} 
        onNeedsVerification={handleNeedsVerification}
        api={api} 
      />
    );
  }

  if (showOnboarding) {
    return (
      <OnboardingFlow 
        user={user} 
        onComplete={handleOnboardingComplete}
        api={api}
      />
    );
  }

  if (showSettings) {
    return (
      <SettingsPage 
        onBack={() => setShowSettings(false)}
        user={user}
        api={api}
        onGmailDisconnected={() => {
          setComplaints([]);
   
        }}
        onNotification={(notification) => setNotification(notification)}
      />
    );
  }
if (showTrustpilotSettings) {
  return (
    <TrustpilotSettings
      api={api}
      onBack={() => setShowTrustpilotSettings(false)}
    />
  );
}
  // Main Dashboard Render
  return (
    <div className="min-h-screen bg-gray-50">
      {notification && (
        <RealtimeNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="bg-white border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white fill-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">TicketDesk</h1>
                  <p className="text-xs text-gray-500">Customer Support Platform</p>
                </div>
              </div>

              <WorkspaceSwitcher
                currentWorkspace={currentWorkspace}
                workspaces={workspaces}
                userSubscription={userSubscription}
                onWorkspaceSwitch={handleWorkspaceSwitch}
                onCreateWorkspace={() => setShowCreateWorkspace(true)}
                onRenameWorkspace={handleRenameWorkspace}
                className="max-w-xs"
              />

              <div className="flex items-center">
                <button
                  onClick={() => setActiveTab('complaints')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeTab === 'complaints'
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Tickets
                </button>
                <button
                  onClick={() => setActiveTab('reviews')}
                  className={`ml-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeTab === 'reviews'
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Reviews
                </button>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`ml-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeTab === 'analytics'
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Analytics
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {user.role === 'ADMIN' && (
                <button
                  onClick={() => setShowSettings(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Settings className="h-5 w-5 text-gray-600" />
                </button>
              )}
              
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-medium text-sm">
                      {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-600 transition-transform ${
                    userMenuOpen ? 'rotate-180' : ''
                  }`} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <span className="text-purple-600 font-medium">
                            {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user.name || 'User'}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {user.email}
                          </p>
                          {user.role && (
                            <p className="text-xs text-purple-600 capitalize">
                              {user.role.toLowerCase()}
                            </p>
                          )}
                          {userSubscription && (
                            <p className="text-xs text-gray-500 capitalize">
                              {userSubscription.plan.toLowerCase()} plan
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="py-2">
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          setShowProfileSettings(true);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                      >
                        <User className="h-4 w-4" />
                        <span>Profile Settings</span>
                      </button>
                      
                      <hr className="my-1 border-gray-100" />
                      
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {userMenuOpen && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setUserMenuOpen(false)}
        ></div>
      )}
      


      {user.role === 'ADMIN' && <TrialBanner api={api} />}
      <UsageBanner api={api} />
      {activeTab === 'complaints' ? (
        <>
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-5 rounded-lg shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">New Tickets</p>
                    <div className="flex items-baseline">
                      <h3 className="text-3xl font-bold text-gray-900">{stats.new}</h3>
                      <span className={`ml-2 text-sm flex items-center ${
                        stats.newChange >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stats.newChange >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {Math.abs(stats.newChange)}%
                      </span>
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-lg shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">In Progress</p>
                    <div className="flex items-baseline">
                      <h3 className="text-3xl font-bold text-gray-900">{stats.inProgress}</h3>
                      <span className={`ml-2 text-sm flex items-center ${
                        stats.inProgressChange >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stats.inProgressChange >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {Math.abs(stats.inProgressChange)}%
                      </span>
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-lg shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Resolved Today</p>
                    <div className="flex items-baseline">
                      <h3 className="text-3xl font-bold text-gray-900">{stats.resolvedToday}</h3>
                      <span className={`ml-2 text-sm flex items-center ${
                        stats.resolvedChange >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stats.resolvedChange >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        +{Math.abs(stats.resolvedChange)}%
                      </span>
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-lg shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Response Time</p>
                    <div className="flex items-baseline">
                      <h3 className="text-3xl font-bold text-gray-900">{stats.avgResponseTime}h</h3>
                      <span className={`ml-2 text-sm flex items-center ${
                        stats.responseTimeChange <= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stats.responseTimeChange <= 0 ? <TrendingDown className="w-3 h-3 mr-1" /> : <TrendingUp className="w-3 h-3 mr-1" />}
                        {Math.abs(stats.responseTimeChange)}%
                      </span>
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="px-6 pb-6">
            <UsageAnalyticsWidget api={api} />
          </div>                  
          <div className="px-6 pb-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search tickets by ID, title, customer, or content..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-sm font-medium transition-colors ${
                    viewMode === 'kanban'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                  Board
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-sm font-medium transition-colors ${
                    viewMode === 'table'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Table className="w-4 h-4" />
                  Table
                </button>
              </div>
              
              <button
                onClick={() => setShowMyCases(!showMyCases)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  showMyCases 
                    ? 'bg-purple-600 text-white hover:bg-purple-700' 
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <UserCheck className="w-4 h-4" />
                My Tickets
              </button>
              
              <div className="relative">
                <button 
                  onClick={() => setFilterMenuOpen(!filterMenuOpen)}
                  className="px-4 py-2 border border-gray-200 rounded-lg flex items-center gap-2 hover:bg-gray-50"
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {(filters.priority !== 'all' || filters.category !== 'all' || filters.assignedTo !== 'all') && (
                    <span className="ml-1 w-2 h-2 bg-purple-600 rounded-full"></span>
                  )}
                </button>
                
                {filterMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-4">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Priority</label>
                        <select 
                          value={filters.priority}
                          onChange={(e) => setFilters({...filters, priority: e.target.value})}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-purple-500"
                        >
                          <option value="all">All Priorities</option>
                          <option value="URGENT">Urgent</option>
                          <option value="HIGH">High</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="LOW">Low</option>
                        </select>
                      </div>
                      
     
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Assigned To</label>
                        <select 
                          value={filters.assignedTo}
                          onChange={(e) => setFilters({...filters, assignedTo: e.target.value})}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-purple-500"
                        >
                          <option value="all">All Assignees</option>
                          {getUniqueAssignees().map(assignee => (
                            <option key={assignee.id} value={assignee.id}>
                              {assignee.name}
                              {assignee.isCurrentUser && ' (Me)'}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="flex gap-2 pt-2 border-t">
                        <button
                          onClick={() => {
                            setFilters({ priority: 'all', assignedTo: 'all' });
                            setShowMyCases(false);
                          }}
                          className="flex-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                        >
                          Clear All
                        </button>
                        <button
                          onClick={() => setFilterMenuOpen(false)}
                          className="flex-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {(searchQuery || filters.priority !== 'all' || filters.category !== 'all' || filters.assignedTo !== 'all' || showMyCases) && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <span className="text-sm text-gray-500">Active filters:</span>
                {showMyCases && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md text-xs flex items-center gap-1">
                    My Cases
                    <button 
                      onClick={() => setShowMyCases(false)}
                      className="ml-1 hover:text-purple-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchQuery && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md text-xs flex items-center gap-1">
                    Search: {searchQuery}
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="ml-1 hover:text-purple-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.priority !== 'all' && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md text-xs flex items-center gap-1">
                    Priority: {filters.priority}
                    <button 
                      onClick={() => setFilters({...filters, priority: 'all'})}
                      className="ml-1 hover:text-purple-900"
                    >
                      ×
                    </button>
                  </span>
                )}
   
                {filters.assignedTo !== 'all' && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md text-xs flex items-center gap-1">
                    Assigned: {getUniqueAssignees().find(a => a.id === filters.assignedTo)?.name || filters.assignedTo}
                    <button 
                      onClick={() => setFilters({...filters, assignedTo: 'all'})}
                      className="ml-1 hover:text-purple-900"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="px-6 pb-8">
            {viewMode === 'kanban' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {STATUS_COLUMNS.map((column) => (
            <StatusColumn
              key={column.id}
              column={column}
              complaints={getComplaintsByStatus(column.id)}
              onComplaintClick={setSelectedComplaint}
              onStatusUpdate={handleStatusUpdate}
              closedTimeRange={closedTimeRange}
              onClosedTimeRangeChange={setClosedTimeRange}
            />
          ))}
              </div>
            ) : (
      <TicketsTable
  complaints={complaints.filter(complaint => {
    // My Cases filter
    if (showMyCases && user) {
      const assignedTo = complaint.assignedTo || complaint.assignedAgent?.id;
      if (!(assignedTo === user.id || assignedTo === user.email || assignedTo === user.name)) {
        return false;
      }
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const subject = (complaint.subject || '').toLowerCase();
      const content = (complaint.content || complaint.description || '').toLowerCase();
      const customerName = getCustomerName(complaint).toLowerCase();
      const customerEmail = getCustomerEmail(complaint).toLowerCase();
      const ticketId = (complaint.ticketId || '').toLowerCase();
      
      if (!(subject.includes(query) || 
            content.includes(query) || 
            customerName.includes(query) ||
            customerEmail.includes(query) ||
            ticketId.includes(query))) {
        return false;
      }
    }
    
    // Priority filter
    if (filters.priority !== 'all') {
      if (complaint.priority?.toUpperCase() !== filters.priority) {
        return false;
      }
    }
    
    // Assigned To filter
    if (filters.assignedTo !== 'all') {
      if (filters.assignedTo === 'unassigned') {
        if (complaint.assignedTo || complaint.assignedAgent) {
          return false;
        }
      } else {
        const assignedId = complaint.assignedTo || complaint.assignedAgent?.id;
        if (assignedId !== filters.assignedTo) {
          return false;
        }
      }
    }
    
    return true;
  })}
                onComplaintClick={setSelectedComplaint}
                onStatusUpdate={handleStatusUpdate}
                getCustomerName={getCustomerName}
                getCustomerEmail={getCustomerEmail}
              />
            )}
          </div>
        </>
      ) : activeTab === 'reviews' ? (
       <ReviewsDashboard 
  api={api} 
  onNavigate={(view) => {
    if (view === 'trustpilot-settings') {
      setShowTrustpilotSettings(true);
    }
  }}
/>
      ) : (
        <AnalyticsDashboard api={api} />
      )}

      <ComplaintModal
        complaint={selectedComplaint}
        isOpen={!!selectedComplaint}
        onClose={() => setSelectedComplaint(null)}
        onComplaintUpdate={handleComplaintUpdate}
        api={api}
        user={user}
      />

      <ProfileSettingsModal
        isOpen={showProfileSettings}
        onClose={() => setShowProfileSettings(false)}
        user={user}
        api={api}
        onUserUpdate={(updatedUser) => setUser(updatedUser)}
      />

      <CreateWorkspaceModal
        isOpen={showCreateWorkspace}
        onClose={() => setShowCreateWorkspace(false)}
        onCreateWorkspace={handleCreateWorkspace}
        userSubscription={userSubscription}
        workspaceLimit={getWorkspaceLimit()}
        currentWorkspaceCount={workspaces.length}
        loading={workspaceLoading}
      />

      <WorkspaceRenameModal
        isOpen={showRenameWorkspace}
        onClose={() => {
          setShowRenameWorkspace(false);
          setWorkspaceToRename(null);
        }}
        workspace={workspaceToRename}
        onRename={handleWorkspaceRenamed}
        api={api}
      />
    </div>
  );
}

export default App;